import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { FaArrowLeft } from "react-icons/fa";
import { Button } from "../../../components/ui";
import apiService from "../../../services/apiService";
import Loader from "../../../components/ui/Loader";
import fileManagerService from "../../../services/fileManagerService";
import { useToast } from "../../../context/ToastContext";

const UploadQuestion = () => {
  const navigate = useNavigate();
  const { id: qSetId } = useParams();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm();

  const { showSuccess, showError, showWarning } = useToast();

  const [loading, setLoading] = useState(false);
  const [streams, setStreams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [conversionStatus, setConversionStatus] = useState(null);

  const [subjectRows, setSubjectRows] = useState([
    {
      subject: "",
      questionStart: "",
      questionEnd: "",
    },
  ]);

  const selectedStream = watch("stream");
  const totalQuestions = Number(watch("totalQuestions") || 0);

  useEffect(() => {
    loadStreams();
  }, []);

  useEffect(() => {
    if (selectedStream) {
      loadSubjects(selectedStream);
    } else {
      setSubjects([]);
    }

    setSubjectRows([
      {
        subject: "",
        questionStart: "",
        questionEnd: "",
      },
    ]);
  }, [selectedStream]);

  const loadStreams = async () => {
    try {
      const res = await apiService.request(
        "/i/ims/coption?option=STREAM&sort=seq%20AS&status=true",
        { method: "GET" },
      );

      setStreams(res?.data || res || []);
    } catch (err) {
      console.log(err);
      showError("Stream load failed.");
    }
  };

  const loadSubjects = async (streamId) => {
    try {
      const res = await apiService.request(
        `/i/ims/subject?stream=${streamId}`,
        { method: "GET" },
      );

      const list = res || [];

      setSubjects(list);
    } catch (err) {
      console.log(err);
      showError("Subject load failed.");
    }
  };

  const getStageText = (activeStep) => {
    if (activeStep === 1) return "PDF converted to text";
    if (activeStep === 2) return "Text parsed by AI";
    if (activeStep === 3) return "Conversion completed";
    if (activeStep === -1) return "Conversion failed";
    return "Conversion pending";
  };

  const usedQuestions = useMemo(() => {
    return subjectRows.reduce((total, row) => {
      const start = Number(row.questionStart);
      const end = Number(row.questionEnd);

      if (!start || !end || end < start) return total;

      return total + (end - start + 1);
    }, 0);
  }, [subjectRows]);

  const canAddMore = totalQuestions > 0 && usedQuestions < totalQuestions;

  const updateRow = (index, key, value) => {
    const rows = [...subjectRows];
    rows[index][key] = value;
    setSubjectRows(rows);
  };

  const addRow = () => {
    if (!canAddMore) return;

    setSubjectRows([
      ...subjectRows,
      {
        subject: "",
        questionStart: "",
        questionEnd: "",
      },
    ]);
  };

  const deleteRow = (index) => {
    if (subjectRows.length === 1) {
      showWarning("At least one subject row is required.");
      return;
    }

    setSubjectRows(subjectRows.filter((_, i) => i !== index));
  };

  const validateSubjectRows = () => {
    if (!selectedStream) {
      showWarning("Please select stream.");
      return false;
    }

    if (!totalQuestions || Number(totalQuestions) <= 0) {
      showWarning("Please enter total questions.");
      return false;
    }

    for (let i = 0; i < subjectRows.length; i++) {
      const row = subjectRows[i];
      const start = Number(row.questionStart);
      const end = Number(row.questionEnd);

      if (!row.subject) {
        showWarning(`Please select subject in row ${i + 1}.`);
        return false;
      }

      if (!start || !end) {
        showWarning(`Please enter question start and end in row ${i + 1}.`);
        return false;
      }

      if (start < 1) {
        showWarning(`Question start should be greater than 0 in row ${i + 1}.`);
        return false;
      }

      if (end < start) {
        showWarning(
          `Question end should be greater than start in row ${i + 1}.`,
        );
        return false;
      }
    }

    if (usedQuestions !== Number(totalQuestions)) {
      showWarning(
        `Subject question range total must be exactly ${totalQuestions}. Current used: ${usedQuestions}.`,
      );
      return false;
    }

    return true;
  };

  async function handleSubmitCustom(data) {
    const file = data.file?.[0];

    if (!validateSubjectRows()) return;

    if (!file) {
      showWarning("Please select a PDF file.");
      return;
    }

    if (!qSetId) {
      showError("QSet ID not found.");
      return;
    }

    try {
      setLoading(true);

      const checkRes = await apiService.request(
        `/i/ims/aistatuslog/conversion/check/${qSetId}?type=Question`,
        { method: "GET" },
      );

      const statusData = checkRes?.data;

      if (statusData?.isRunning) {
        setConversionStatus(statusData);
        setLoading(false);

        showWarning(
          `A conversion is already running for this QSet. Current stage: ${
            statusData.message || getStageText(statusData.activeStep)
          }`,
        );

        return;
      }

      const formData = new FormData();
      formData.append("pdfFile", file);
      formData.append("qSet", qSetId);
      const cleanSubjectRows = subjectRows.map((row) => ({
        subject: row.subject,
        questionStart: Number(row.questionStart),
        questionEnd: Number(row.questionEnd),
      }));
      const subjectsQuery = encodeURIComponent(
        JSON.stringify(cleanSubjectRows),
      );
      const uploadRes = await fileManagerService.uploadFileLocalTest(
        formData,
        `/test/upload/pdf/question/${qSetId}?stream=${data.stream}&totalQuestions=${data.totalQuestions}&subjects=${subjectsQuery}`,
      );

      if (uploadRes?.alreadyRunning || uploadRes?.data?.isRunning) {
        const statusData = uploadRes?.data;

        setConversionStatus(statusData);
        setLoading(false);

        showWarning(
          `A conversion is already running for this QSet. Current stage: ${
            statusData.message || getStageText(statusData.activeStep)
          }`,
        );

        return;
      }

      setLoading(false);
      reset();

      setSubjectRows([
        {
          subject: "",
          questionStart: "",
          questionEnd: "",
        },
      ]);

      const jobId = uploadRes?.jobId || uploadRes?.data?.jobId;

      if (uploadRes?.success === true || jobId) {
        setConversionStatus({
          isRunning: true,
          jobId,
          activeStep: 0,
          message: "Conversion started",
        });

        showSuccess("File uploaded successfully. Conversion has started.");
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      showError("Upload failed.");
    }
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Add Questions</h1>

        <Button
          onClick={() => navigate("/tools/qSet")}
          variant="light"
          size="sm"
          icon={FaArrowLeft}
        >
          Back to List
        </Button>
      </div>

      {/* {conversionStatus?.isRunning && (
        <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 p-4">
          <p className="font-semibold text-yellow-800">
            Conversion already running
          </p>

          <p className="text-sm text-yellow-700 mt-1">
            Stage:{" "}
            {conversionStatus.message ||
              getStageText(conversionStatus.activeStep)}
          </p>

          {conversionStatus.jobId && (
            <p className="text-xs text-yellow-600 mt-1">
              Job ID: {conversionStatus.jobId}
            </p>
          )}
        </div>
      )} */}

      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="p-4">
          <form onSubmit={handleSubmit(handleSubmitCustom)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3">
              <div>
                <label className="block text-gray-700 mb-2">
                  Stream <span className="text-red-500">*</span>
                </label>

                <select
                  className="w-full border rounded p-2"
                  disabled={loading}
                  {...register("stream", {
                    required: "Stream is required",
                  })}
                >
                  <option value="">Select Stream</option>
                  {streams.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.value || item.valueAlias} ({item.code})
                    </option>
                  ))}
                </select>

                {errors.stream && (
                  <p className="text-red-500 text-sm">
                    {errors.stream.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-gray-700 mb-2">
                  Total Questions <span className="text-red-500">*</span>
                </label>

                <input
                  type="number"
                  min="1"
                  className="w-full border rounded p-2"
                  disabled={loading || !selectedStream}
                  placeholder="Enter total questions"
                  {...register("totalQuestions", {
                    required: "Total questions is required",
                    min: {
                      value: 1,
                      message: "Minimum 1 question required",
                    },
                  })}
                />

                {errors.totalQuestions && (
                  <p className="text-red-500 text-sm">
                    {errors.totalQuestions.message}
                  </p>
                )}
              </div>
            </div>

            {selectedStream && totalQuestions > 0 && (
              <div className="p-3">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold">Subject Question Range</h3>

                  <span className="text-sm text-gray-600">
                    Used: {usedQuestions} / {totalQuestions}
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border p-2 text-left">Subject</th>
                        <th className="border p-2 text-left">Question Start</th>
                        <th className="border p-2 text-left">Question End</th>
                        <th className="border p-2 text-center">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {subjectRows.map((row, index) => (
                        <tr key={index}>
                          <td className="border p-2">
                            <select
                              className="w-full border rounded p-2"
                              value={row.subject}
                              disabled={loading}
                              onChange={(e) =>
                                updateRow(index, "subject", e.target.value)
                              }
                            >
                              <option value="">Select Subject</option>
                              {subjects.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.name ||
                                    item.title ||
                                    item.value ||
                                    item.code}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="border p-2">
                            <input
                              type="number"
                              min="1"
                              className="w-full border rounded p-2"
                              value={row.questionStart}
                              disabled={loading}
                              onChange={(e) =>
                                updateRow(
                                  index,
                                  "questionStart",
                                  e.target.value,
                                )
                              }
                            />
                          </td>

                          <td className="border p-2">
                            <input
                              type="number"
                              min="1"
                              className="w-full border rounded p-2"
                              value={row.questionEnd}
                              disabled={loading}
                              onChange={(e) =>
                                updateRow(index, "questionEnd", e.target.value)
                              }
                            />
                          </td>

                          <td className="border p-2 text-center">
                            <button
                              type="button"
                              disabled={loading || subjectRows.length === 1}
                              onClick={() => deleteRow(index)}
                              className={`px-3 py-1 rounded text-white ${
                                subjectRows.length === 1
                                  ? "bg-gray-400 cursor-not-allowed"
                                  : "bg-red-500 hover:bg-red-600"
                              }`}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button
                  type="button"
                  disabled={!canAddMore || loading}
                  onClick={addRow}
                  className={`mt-3 px-4 py-2 rounded text-white ${
                    !canAddMore || loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-600"
                  }`}
                >
                  Add More
                </button>
              </div>
            )}

            <div className="p-3">
              <label className="block text-gray-700 mb-2">
                Select PDF File <span className="text-red-500">*</span>
              </label>

              <input
                type="file"
                className="m-2"
                accept=".pdf"
                disabled={loading || !selectedStream || !totalQuestions}
                {...register("file", {
                  required: "File is required",
                })}
              />

              {errors.file && (
                <p className="text-red-500 text-sm">{errors.file.message}</p>
              )}
            </div>

            <div className="p-3 flex items-center gap-3">
              <button
                type="submit"
                disabled={loading}
                className={`font-thin text-white p-2 rounded m-2 ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-500 hover:bg-green-600"
                }`}
              >
                {loading ? "Checking..." : "Upload"}
              </button>

              {loading && <Loader />}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UploadQuestion;
